import React, { FunctionComponent, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';

import { ZoneSettings } from '@paragon/contract-library';

import { setInsertionComponentZoneSettings } from '../../../../store/actions/editorActions';
import { getActiveFeaturePatternTrussSettings } from '../../../../store/selectors/activeComponentFeaturePatternSettingsSelectors';
import { settingsShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { ChaseSettingsPanel } from './ChaseSettings';
import { WebbingSettings } from './WebbingSettings';

interface SingleZoneSettingsPanelProps {
  disabled?: boolean;

  value?: ZoneSettings;
  onChange(newValue: ZoneSettings): void;

  readonly hideWebStart?: boolean;
}

export const SingleZoneSettingsPanel: FunctionComponent<SingleZoneSettingsPanelProps> = (props) => {
  const forceDisabled = useAppSelector(settingsShouldBeViewOnly);
  const disabled = props.disabled || forceDisabled;

  if (!props.value) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <WebbingSettings
        zone={props.value}
        updateZone={props.onChange}
        disabled={disabled}
        hideWebStart={props.hideWebStart}
      />
      <ChaseSettingsPanel
        value={props.value.chaseSettings}
        onChange={(chaseSettings) => {
          props.onChange({
            ...props.value!,
            chaseSettings,
          });
        }}
        disabled={disabled}
      />
    </Box>
  );
};

interface ZoneSettingsPanelProps {
  values: readonly ZoneSettings[];
  onChange(modifiedZone: ZoneSettings, zoneIndex: number): void;
}

export const ZoneSettingsPanel: FunctionComponent<ZoneSettingsPanelProps> = (props) => {
  const [activeZoneGuid, setActiveZoneGuid] = useState(props.values[0] ? props.values[0].guid : '');

  const activeZoneIndex = props.values.findIndex((zone) => zone.guid === activeZoneGuid);
  const activeZone = props.values[activeZoneIndex];

  const chooseZoneSelect = (
    <FormControl sx={{ m: 'auto', marginTop: '16px', width: 200 }}>
      <InputLabel htmlFor="selected-zone">Selected Zone</InputLabel>
      <Select
        size="small"
        variant="outlined"
        value={activeZoneGuid}
        onChange={(event) => setActiveZoneGuid(event.target.value as string)}
        inputProps={{
          name: 'zone',
          id: 'selected-zone',
        }}
        label="Selected Zone"
      >
        {props.values.map((zoneSettings, index) => (
          <MenuItem value={zoneSettings.guid} key={zoneSettings.guid}>
            {`Zone ${index + 1}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {chooseZoneSelect}
      {activeZone && (
        <SingleZoneSettingsPanel value={activeZone} onChange={(value) => props.onChange(value, activeZoneIndex)} />
      )}
    </Box>
  );
};

export const InsertionComponentZoneSettingsPanel: FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const featurePatternSettings = useAppSelector(getActiveFeaturePatternTrussSettings);

  return (
    <ZoneSettingsPanel
      values={featurePatternSettings!.zoneSettings}
      onChange={(value, zoneIndex) => {
        dispatch(setInsertionComponentZoneSettings(zoneIndex, value));
      }}
    />
  );
};
